// Optimized side panel component using hierarchical filter endpoint
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFilters } from '../../../contexts/FiltersContext';
import analyticsService from '../../../services/analyticsApi';
import { Box, IconButton, Button, CircularProgress, LinearProgress, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useAuthStore } from '../../../store/authStore';
import { AccountType } from '../../../types/auth';
import { PanelSectionList, type PanelData } from './_shared';
import { buildFilterParams } from './filterParams';
import { useAnalyticsRefresh } from '../../../contexts/AnalyticsRefreshContext';

// Surface-level relabel: show "NA" for the default "main" value (id unchanged for API).
const mainRelabel = (c: string): string =>
  typeof c === 'string' && c.toLowerCase().includes('main') ? 'NA' : c;

// Prune any current selections that are no longer present in the new option list.
// Only invokes the setter when the selection set actually changed (preserves the
// original conditional-setter behavior).
export const pruneInvalid = (
  current: Array<{ id: string }>,
  options: string[],
  setter: (data: any) => void
): void => {
  const valid = current.filter((c) => options.includes(c.id));
  if (valid.length !== current.length) {
    setter(valid);
  }
};

// Sub-customers and cities are cursor-paged by their own sections (see
// usePagedOptions), so the hierarchy call must skip them. Their sections always
// render, and their stored selections are never pruned by a hierarchy reload.
const PAGED_EXCLUDE = 'customers,originCities,destinationCities';

const pagedPanel = (key: string, label: string): PanelData => ({
  type: 'checklist',
  key,
  label,
  values: [],
});
const SUB_CUSTOMER_PANEL = pagedPanel('subCustomer', 'Sub Customer');
const CITY_ORIGIN_PANEL = pagedPanel('cityOrigin', 'Origin City');
const CITY_DESTINATION_PANEL = pagedPanel('cityDestination', 'Destination City');

// A dimension option is either a bare string (id === value, e.g. cities/countries)
// or an { id, value } pair (e.g. sub-customers, whose id is the raw
// customer_reference and value is a display label like "CODE (Name)").
type DimensionOption = string | { id: string; value: string };

const normalizeOption = (o: DimensionOption): { id: string; value: string } =>
  typeof o === 'string' ? { id: o, value: o } : o;

// Build a checklist PanelData from a list of options. The id is used for
// filtering/selection; value (relabelled) is what the user sees.
const buildChecklist = (
  key: string,
  label: string,
  options: Array<{ id: string; value: string }>,
  relabel: (c: string) => string
): PanelData => ({
  type: 'checklist',
  key,
  label,
  values: options.map((o) => ({ id: o.id, value: relabel(o.value), isChecked: false })),
});

// Process one checklist dimension: prune invalid selections then either set the
// built PanelData or clear it (and the stored selections) when empty.
const processChecklistDimension = (args: {
  options: DimensionOption[] | undefined;
  current: Array<{ id: string }>;
  key: string;
  label: string;
  relabel: (c: string) => string;
  setData: (d: PanelData | null) => void;
  setSelections: (data: any) => void;
}): void => {
  const { options, current, key, label, relabel, setData, setSelections } = args;
  if (options && options.length > 0) {
    const normalized = options.map(normalizeOption);
    pruneInvalid(current, normalized.map((o) => o.id), setSelections);
    setData(buildChecklist(key, label, normalized, relabel));
  } else {
    setData(null);
    if (current.length > 0) {
      setSelections([]);
    }
  }
};

const SidePanelOptimized: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const useFilterContext = useFilters();
  const refresh = useAnalyticsRefresh();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  // Sections stay mounted across reloads (the paged lists keep their scroll and
  // pages); the full-panel spinner is only for the very first load.
  const [hasLoaded, setHasLoaded] = useState(false);

  // Determine current page based on route
  const currentPage = (() => {
    if (location.pathname.includes('/analytics')) return 'analytics';
    if (location.pathname.includes('/emissions-dashboard')) return 'emissions';
    return null;
  })();

  // Helper function to check if filter should be visible based on user role
  const shouldShowFilter = (filterType: string): boolean => {
    if (!user) return false;

    switch (user.accountType) {
      case AccountType.MasterAdmin:
      case AccountType.CarbonCareAdmin:
        // MasterAdmin can see all filters
        return true;

      case AccountType.LicenseeAdmin:
      case AccountType.User:
        // Can see all except cannot change licensee (backend enforces this)
        return filterType !== 'licensee';

      case AccountType.SubCompanyAdmin:
        // Can see customer, branch, geo, transport (backend enforces licensee + sub-company)
        return !['licensee', 'subCompany'].includes(filterType);

      case AccountType.BranchAdmin:
        // Can see customer, geo, transport (backend enforces licensee + sub-company + branch)
        return !['licensee', 'subCompany', 'branch'].includes(filterType);

      case AccountType.SubCustomerAdmin:
        // Can see sub-company, geo, transport (backend enforces licensee + sub-customer)
        // Sub-company filter allows filtering when sub-customer belongs to multiple sub-companies
        return ['subCompany', 'countryOrigin', 'countryDestination', 'cityOrigin', 'cityDestination', 'transportMode'].includes(filterType);

      // case AccountType.SubCustomer:
      //   // Can see sub-customer (their customer refs), sub-company (companies they belong to), geo, transport
      //   return ['subCustomer', 'subCompany', 'countryOrigin', 'countryDestination', 'cityOrigin', 'cityDestination', 'transportMode'].includes(filterType);

      default:
        return false;
    }
  };

  // Panel data states
  const [licenseeData, setLicenseeData] = useState<PanelData | null>(null);
  const [subCompanyData, setSubCompanyData] = useState<PanelData | null>(null);
  const [branchData, setBranchData] = useState<PanelData | null>(null);
  const [countryOriginData, setCountryOriginData] = useState<PanelData | null>(null);
  const [countryDestinationData, setCountryDestinationData] = useState<PanelData | null>(null);
  const [transportModeData, setTransportModeData] = useState<PanelData | null>(null);

  // Debounce timer - use ref to avoid stale closure issues
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Mirror the latest refresh cycle so a debounced fetch reports completion for
  // the cycle that was active when it actually fired (not when it was scheduled).
  const cycleIdRef = useRef(0);
  cycleIdRef.current = refresh.cycleId;

  // Load all filters using the optimized hierarchical endpoint
  const loadAllFilters = async (delay: number = 0) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      const cycle = cycleIdRef.current;
      try {
        setIsLoading(true);

        // Scope = ALL current selections; the paged dimensions are fetched separately.
        const params = { ...buildFilterParams(useFilterContext), exclude: PAGED_EXCLUDE };

        // Single API call to get all (non-paged) filter options
        const { data } = await analyticsService.getData(
          analyticsService.endpoints.filters.filterHierarchy,
          params
        );

        if (data.success) {
          const filters = data.filters;
          const id = (c: string) => c;

          // Transform API response to PanelData format

          // Licensees (checklist) - Always show all licensees, support multiple selection
          if (filters.licensees && filters.licensees.length > 0) {
            setLicenseeData({
              type: 'checklist',
              key: 'licensee',
              label: 'Licensee',
              values: filters.licensees.map((l: any) => ({ ...l, isChecked: false })),
            });
          }

          // Sub Companies (checklist) - Filtered by licensee + customer
          processChecklistDimension({
            options: filters.subCompanies,
            current: useFilterContext.subCompanyData,
            key: 'subCompany',
            label: 'Sub Company',
            relabel: mainRelabel,
            setData: setSubCompanyData,
            setSelections: useFilterContext.setSubCompanyData,
          });

          // Branches (checklist) - Filtered by licensee + customer + sub-company
          processChecklistDimension({
            options: filters.branches,
            current: useFilterContext.branchData,
            key: 'branch',
            label: 'Branch',
            relabel: mainRelabel,
            setData: setBranchData,
            setSelections: useFilterContext.setBranchData,
          });

          // Country Origin (checklist) - Filtered by all hierarchy + geo
          processChecklistDimension({
            options: filters.originCountries,
            current: useFilterContext.countryOriginData,
            key: 'countryOrigin',
            label: 'Origin Country',
            relabel: id,
            setData: setCountryOriginData,
            setSelections: useFilterContext.setCountryOriginData,
          });

          // Country Destination (checklist)
          processChecklistDimension({
            options: filters.destinationCountries,
            current: useFilterContext.countryDestinationData,
            key: 'countryDestination',
            label: 'Destination Country',
            relabel: id,
            setData: setCountryDestinationData,
            setSelections: useFilterContext.setCountryDestinationData,
          });

          // Transport Modes (checklist)
          processChecklistDimension({
            options: filters.transportModes,
            current: useFilterContext.transportModeData,
            key: 'transportMode',
            label: 'Transport Mode',
            relabel: id,
            setData: setTransportModeData,
            setSelections: useFilterContext.setTransportModeData,
          });
        }
      } catch (error) {
        console.error('Failed to load filter hierarchy:', error);
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
        // Report this cycle done so the barrier can clear once the main screen
        // also finishes. No-op (stale-guarded) for the initial mount load.
        refresh.endPanel(cycle);
      }
    }, delay);
  };

  // Register with the refresh coordinator so, within one filter-refresh cycle,
  // the main screen waits for the panel and vice-versa. No-op without a provider
  // (e.g. the emissions dashboard), where the panel keeps its own loader.
  useEffect(() => refresh.registerPanel(), [refresh.registerPanel]);

  // Initial load + cleanup timer on unmount
  useEffect(() => {
    loadAllFilters(0);
    if (initialLoad) setInitialLoad(false);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Reload when ANY filter changes - cascades all filter options
  useEffect(() => {
    if (!initialLoad) {
      loadAllFilters(300);
    }
  }, [
    useFilterContext.licenseeData,
    useFilterContext.subCustomerData,
    useFilterContext.subCompanyData,
    useFilterContext.branchData,
    useFilterContext.countryOriginData,
    useFilterContext.countryDestinationData,
    useFilterContext.cityOriginData,
    useFilterContext.cityDestinationData,
    useFilterContext.transportModeData,
  ]);

  // No-op: useEffect handles all filter change reloads
  const handleSidePanelSelection = (_key: string) => {};

  const handleClearAll = () => {
    // Clear all filter types
    useFilterContext.clearAllType('licensee');
    useFilterContext.clearAllType('subCompany');
    useFilterContext.clearAllType('branch');
    useFilterContext.clearAllType('subCustomer');
    useFilterContext.clearAllType('countryOrigin');
    useFilterContext.clearAllType('countryDestination');
    useFilterContext.clearAllType('cityOrigin');
    useFilterContext.clearAllType('cityDestination');
    useFilterContext.clearAllType('transportMode');
    loadAllFilters(0);
  };

  return (
    <Box
      sx={{
        width: isOpen ? 300 : 60,
        height: '100vh',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        '@media print': {
          display: 'none',
        },
      }}
    >
      {/* Header - Go Back + Clear Filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOpen ? 'space-between' : 'center',
          px: isOpen ? 1.5 : 0.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {isOpen && user && (
          <Box
            onClick={() => navigate(
              user.accountType === AccountType.BranchAdmin || user.accountType === AccountType.SubCustomerAdmin
                ? '/settings'
                : '/dashboard'
            )}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: '#90bf45',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 0.5,
              '&:hover': {
                bgcolor: 'rgba(144, 191, 69, 0.08)',
                borderColor: '#90bf45',
              },
              transition: 'all 0.2s',
            }}
          >
            {user.accountType === AccountType.BranchAdmin || user.accountType === AccountType.SubCustomerAdmin ? (
              <SettingsIcon sx={{ fontSize: 16 }} />
            ) : (
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            )}
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem', color: 'text.primary' }}>
              {user.accountType === AccountType.BranchAdmin || user.accountType === AccountType.SubCustomerAdmin
                ? 'Settings'
                : 'Back to Home Screen'}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isOpen && (
            <Tooltip title="Clear all filters" placement="bottom">
              <IconButton
                size="small"
                onClick={handleClearAll}
                sx={{
                  color: '#d32f2f',
                  '&:hover': {
                    bgcolor: 'rgba(211, 47, 47, 0.08)',
                  },
                }}
              >
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Navigate to other page */}
      {isOpen && (
        <Box
          sx={{
            py: 1,
            px: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={currentPage === 'analytics' ? <TableChartIcon /> : <BarChartIcon />}
            onClick={() => navigate(currentPage === 'analytics' ? '/emissions-dashboard' : '/analytics')}
            sx={{
              width: '100%',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              color: '#90bf45',
              borderColor: '#90bf45',
              '&:hover': {
                borderColor: '#7da339',
                bgcolor: 'rgba(144, 191, 69, 0.08)',
              },
            }}
          >
            {currentPage === 'analytics' ? 'Go To Analytics Data' : 'Go To Analytics Charts'}
          </Button>
        </Box>
      )}

      {/* Filters Content */}
      {isOpen && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {!hasLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <LinearProgress
                aria-label="Refreshing filters"
                sx={{
                  mb: 1,
                  visibility: isLoading || refresh.isFilterRefreshing ? 'visible' : 'hidden',
                }}
              />
              <PanelSectionList
                licenseeData={licenseeData}
                subCompanyData={subCompanyData}
                branchData={branchData}
                subCustomerData={SUB_CUSTOMER_PANEL}
                countryOriginData={countryOriginData}
                countryDestinationData={countryDestinationData}
                cityOriginData={CITY_ORIGIN_PANEL}
                cityDestinationData={CITY_DESTINATION_PANEL}
                transportModeData={transportModeData}
                handleSidePanelSelection={handleSidePanelSelection}
                shouldShowFilter={shouldShowFilter}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SidePanelOptimized;
